import { apiInstance, objectToFormData } from 'common/utils/axios';
import { ResponseType, Product, Family } from './types';
import imageCompression from 'browser-image-compression';

export interface ProductFilters {
  families: string;
  partners: string;
  priceList: string;
  partnerPriceList: string;
  search: string;
  id: string;
  partner_id: string;
  visibility: React.ReactText[];
  availability: React.ReactText[];
  merchandise_bonus: React.ReactText[];
}

export interface GetProductsRequest {
  page: number;
  sorts: string;
  filters: Partial<ProductFilters>;
  /** Tamaño de página en el listado (default 50 en cliente). */
  rows?: number;
}

export type PostProduct = Pick<
  Product,
  | 'id'
  | 'partner_sku'
  | 'ean_upc'
  | 'partner_id'
  | 'family_id'
  | 'name'
  | 'description'
  | 'brand_name'
  | 'base_price'
  | 'fractionable'
  | 'apt_fractionable'
  | 'available'
  | 'visible'
  | 'image'
  | 'measure_unit_id'
  | 'presentation_id'
  | 'sales_unit'
  | 'bulk_id'
  | 'bulk_presentation'
  | 'sell_and_validation_condition_id'
  | 'created_at'
  | 'updated_at'
>;

export type PatchProducts = Pick<Product, 'id' | 'available' | 'visible'>[];

const getCompressedFile = async (file: File | undefined) => {
  if (file) {
    const options = {
      maxSizeMB: 3,
      maxWidthOrHeight: 184,
      useWebWorker: true
    };

    try {
      return await imageCompression(file, options);
    } catch (error) {
      console.log(error);
    }
  }

  return '';
};

function sortFamiliesByNameAsc(families: Family[]): Family[] {
  return [...families].sort((a, b) => {
    const na = a.name != null ? String(a.name) : '';
    const nb = b.name != null ? String(b.name) : '';
    return na.localeCompare(nb, 'es', { sensitivity: 'base', numeric: true });
  });
}

/**
 * Categorías (familias) para el modal de edición en landing.
 * GET `/products/families?rows=100&order=name:asc` — respuesta puede ser `{ data: Family[] }` o `Family[]`.
 * Orden A→Z por nombre en cliente (locale `es`) porque el backend no siempre devuelve bien el sort.
 */
export const getProductFamiliesForLandingSelector = async (): Promise<Family[]> => {
  const { data } = await apiInstance.get<ResponseType<Family[]> | Family[]>(`/products/families`, {
    params: { rows: 100, order: 'name:asc' }
  });
  let list: Family[] = [];
  if (Array.isArray(data)) {
    list = data;
  } else if (data != null && typeof data === 'object' && Array.isArray((data as ResponseType<Family[]>).data)) {
    list = (data as ResponseType<Family[]>).data;
  }
  return sortFamiliesByNameAsc(list);
};

const DEFAULT_PRODUCTS_PAGE_ROWS = 50;

export const getProducts = async (request: Partial<GetProductsRequest> = {}) => {
  const sorts =
    request.sorts != null && String(request.sorts).trim() !== '' ? request.sorts : 'name:asc';
  const rows = request.rows != null && !Number.isNaN(Number(request.rows)) ? Number(request.rows) : DEFAULT_PRODUCTS_PAGE_ROWS;
  const params = { ...request, sorts, rows };
  const { data } = await apiInstance.get<ResponseType<Product[]>>('/products', { params });
  return data;
};

const buildProductFormData = async (request: PostProduct) => {
  const { image, ...rest } = request;
  const formData = objectToFormData(rest);
  if (image instanceof File) {
    const compressedImage = await getCompressedFile(image);
    formData.append('image', compressedImage);
  }

  return formData;
};

export const createProduct = async (request: PostProduct) => {
  const formData = await buildProductFormData(request);
  const { data } = await apiInstance.post<ResponseType<Product>>('/products', formData, {
    headers: { 'content-type': 'multipart/form-data' }
  });
  return data;
};

export const updateProduct = async (id: string, request: PostProduct) => {
  const formData = await buildProductFormData(request);

  const response = await apiInstance.put(`/products/${id}`, formData, {
    headers: { 'content-type': 'multipart/form-data' }
  });

  return response.status === 200;
};

export const patchProducts = async (request: PatchProducts) => {
  const { data } = await apiInstance.request<ResponseType<{ count: number }>>({
    method: 'PATCH',
    url: '/products',
    data: request
  });

  return data;
};

export const deleteProducts = async (request: number[]) => {
  const { data } = await apiInstance.request<ResponseType<{ count: number }>>({
    method: 'DELETE',
    url: '/products',
    data: request
  });

  return data;
};

export const deleteProduct = async (id: number) => {
  const { data } = await apiInstance.request({
    method: 'DELETE',
    url: `/products/${id}`
  });

  return data;
};

/** Auditoría de listado base (última actualización en landing). */
export interface ProductAudit {
  created_at: string;
  user_roles?: string[];
  partner_legal_name?: string | null;
}

/**
 * GET `/products/audits?sorts=created_at:desc` — primer ítem = más reciente.
 * Respuesta típica: `{ data: ProductAudit[] }` o arreglo plano.
 */
export const getProductAudits = async (): Promise<ResponseType<ProductAudit[]> | ProductAudit[]> => {
  const { data } = await apiInstance.get<ResponseType<ProductAudit[]> | ProductAudit[]>(
    '/products/audits',
    { params: { sorts: 'created_at:desc' } }
  );
  return data;
};

export interface ProductItemsUpdateItemRow {
  id: number;
  price: number;
}

export interface ProductItemsUpdateProductRow {
  id: number;
  items: ProductItemsUpdateItemRow[];
}

export interface PostProductsItemsUpdatePayload {
  partner_id: number;
  products: ProductItemsUpdateProductRow[];
}

/** POST `/products/items/update` — precios por ítem de lista (IDs de `partnerPriceListItems`). */
export const postProductsItemsUpdate = async (payload: PostProductsItemsUpdatePayload) => {
  return apiInstance.post<ResponseType<unknown>>('/products/items/update', payload);
};

export interface PutProductLandingPayload {
  name: string;
  family_id: number;
}

/** PUT `/products/:id` — solo nombre y `family_id` (JSON). */
export const putProductLanding = async (id: number, payload: PutProductLandingPayload) => {
  return await apiInstance.put<ResponseType<Product>>(`/products/${id}`, {
    name: payload.name,
    family_id: payload.family_id
  });
};

/** Laravel `image` rule + `max:3072` (valor en kilobytes → 3 MiB) */
export const PRODUCT_LANDING_IMAGE_MAX_BYTES = 3072 * 1024;

export interface PostProductLandingImagePayload {
  /**
   * Igual que `updateProductImage` en upload-image-reference.js: un solo campo `image` en el FormData,
   * sin `File`/nombre largo: el blob del canvas (PNG) con nombre de parte `blob` para que Symfony
   * reciba `UploadedFile` con `originalName` === `blob` como en jQuery.
   */
  image: Blob;
}

/**
 * POST `/products/:id/image` — mismo cuerpo que `$.ajax` legacy (solo FormData + Bearer).
 * No se envían query params (el `_` de axios llegaba mezclado al `$request` en Laravel).
 */
export const postProductLandingImage = async (id: number, payload: PostProductLandingImagePayload) => {
  const formData = new FormData();
  const blob = payload.image;
  /** Tercer argumento = nombre del archivo en multipart (legacy: `originalName` => `blob`) */
  formData.append('image', blob, 'blob');

  return apiInstance.post<ResponseType<Product>>(`/products/${id}/image`, formData, {
    headers: {
      Accept: '*/*',
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: '0'
    },
    maxContentLength: Infinity,
    transformRequest: [
      (data, headers) => {
        if (typeof FormData !== 'undefined' && data instanceof FormData) {
          delete headers['Content-Type'];
          delete headers['content-type'];
          return data;
        }
        return data;
      }
    ]
  });
};
